import React, { useCallback, useContext, useMemo } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { rem } from 'polished'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { SEARCH_VERTICALS } from 'constants/search'
import { useAppDispatch } from 'hooks/reduxHooks'
import { CruiseDurationOptions } from './Inputs/CruiseWhenSelect/CruiseWhenSelectHowLong'
import TextLink from 'components/Luxkit/TextLink'
import Group from 'components/utils/Group'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import ModalContext from 'contexts/ModalContext'
import CruiseSearchFiltersDrawer from './CruiseSearchExtraFiltersDrawer'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { CruiseSearchFilterKeys } from 'constants/cruise'
import { replace } from 'connected-react-router'
import { useLocation } from 'react-router'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { take } from 'lib/array/arrayUtils'

const SectionContainer = styled(LayoutContainer)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: sticky;
  top: 0;
  z-index: 2;
  margin-top: 0;
`

const SearchContainer = styled(VerticalSpacer)`
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} 0;
  }
`

interface Props {
  filters: App.OfferListFilters;
  className?: string;
}

function getDestinationName(
  locations: App.CruiseSearchFacet[],
  locationId?: string,
  locationIds?: string[],
) {
  const selectedItems = locations.filter(({ value }) => (
    value === locationId ||
    locationIds?.includes(value || '')
  )).map(({ name }) => name.split(',')[0])

  if (selectedItems.length > 1) {
    return `${selectedItems[0]} +${selectedItems.length - 1}`
  }

  return selectedItems[0]
}

function CruiseSearchFiltersMobile(props: Props) {
  const { className, filters } = props
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const openCruisesSearch = useCallback(() => { toggleSearchVertical(SEARCH_VERTICALS.CRUISES) }, [toggleSearchVertical])
  const [cruiseLines] = useCruiseSearchFacets({ facetTypes: ['cruise_lines'] })
  const [destinations] = useCruiseSearchFacets({ facetTypes: ['destination_ports'] })
  const [departures] = useCruiseSearchFacets({ facetTypes: ['departure_ports'] })

  const {
    departureMonths: flexibleMonths,
    durationMax,
    durationMin,
  } = filters
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { search } = useLocation()
  const { heights } = useContext(HeaderHeightContext)

  const firstLineFilters = useMemo(() => {
    const destinationName = getDestinationName(destinations, filters.destinationId, filters.destinationIds)
    const departureName = getDestinationName(departures, filters.departurePlaceId, filters.departureIds)
    const data : string[] = []

    data.push(departureName ? `Starts in ${departureName}` : 'All departure ports')
    data.push(destinationName ? `Cruising to ${destinationName}` : 'All destinations')

    return data.join('. ')
  }, [filters.destinationId, filters.destinationIds, filters.departurePlaceId, filters.departureIds, destinations, departures])

  const secondLineFilters = useMemo(() => {
    const data: string[] = []

    const allCruiseLineNames = cruiseLines?.filter(cruiseLine => filters.cruiseLines?.includes(cruiseLine.value ?? ''))
      .map(cruiseLine => cruiseLine.name)

    if (allCruiseLineNames.length > 0) {
      const [firstName] = allCruiseLineNames
      const more = allCruiseLineNames.length > 1 ? `+${allCruiseLineNames.length - 1}` : ''
      let cruiseLineNames = firstName && `${firstName}`

      if (more) {
        cruiseLineNames = `${cruiseLineNames} ${more}`
      }

      data.push(cruiseLineNames)
    } else {
      data.push('All cruise lines')
    }

    const durationOption = CruiseDurationOptions.find(opt => opt.min >= durationMin && opt.max <= durationMax)
    data.push(durationOption?.name || 'Any duration')

    const departureMonths = flexibleMonths?.filter(Boolean) ?? []
    if (departureMonths.length) {
      const limitVisible = 1
      const formattedMonths = departureMonths?.map(month => moment(month, 'YYYY-MM').format('MMM YYYY'))
      const more = formattedMonths.length > limitVisible ? `+${formattedMonths.length - limitVisible}` : ''
      const dates = take(formattedMonths, limitVisible)

      const dateValues = more ? `${dates.join(', ')} ${more}` : dates.join(', ')

      if (dateValues) {
        data.push(dateValues)
      }
    }

    return data.join('. ')
  }, [cruiseLines, flexibleMonths, durationMin, durationMax, filters.cruiseLines])

  const facetParams = useMemo(() => ({
    facetTypes: ['ship_names', 'cabin_types', 'special_offers', 'lux_exclusive', 'cruise_prices'],
    cruiseLines: filters.cruiseLines,
    departurePlaceId: filters.departurePlaceId,
    departureIds: filters.departureIds,
    rateCodes: filters.rateCodes,
    destinationId: filters.destinationId,
    destinationIds: filters.destinationIds,
    departureMonths: filters.departureMonths,
    durationMin: filters.durationMin,
    durationMax: filters.durationMax,
    departureStartDate: filters.departureStartDate,
    departureEndDate: filters.departureEndDate,
  } as unknown as App.CruiseSearchFacetParams), [filters])

  const [facets] = useCruiseSearchFacets(facetParams)

  const onShowFilters = useCallback(async() => {
    const newFilters = await showModal<Record<string, any>>(
      <CruiseSearchFiltersDrawer listFilters={filters} />,
    )
    const priceFacet = facets.find(facet => facet.category === 'Price')
    if (newFilters.price?.start) {
      newFilters.minPrice = newFilters.price?.start
    }

    if (newFilters.price?.end && newFilters.price?.end !== priceFacet?.max) {
      newFilters.maxPrice = newFilters.price?.end
    }
    delete newFilters.price
    const urlValues = Object.entries(newFilters).filter(([_, value]) => !!value).map(([key, value]) => ({ paramName: key, paramValue: value }))
    // reset all our possible filter keys
    let nextSearch = deleteSearchParams(search, 'sortBy', ...CruiseSearchFilterKeys)
    // then take whatever is now applicable
    nextSearch = setManySearchParamValues(nextSearch, urlValues)

    dispatch(replace({ search: nextSearch.toString() }))
  }, [dispatch, filters, search, showModal, facets])

  return (<SectionContainer className={className} style={{ top: `${heights.siteStickyBanner}px` }}>
    <SearchContainer gap={12}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={12} verticalAlign="center">
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold">
            {firstLineFilters}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {secondLineFilters}
          </BodyText>
        </VerticalSpacer>
        <TextLink size="medium" onClick={openCruisesSearch}>Edit</TextLink>
      </Group>
      <TextButton
        kind="secondary"
        variant="dark"
        startIcon={<LineFilterAltIcon />}
        fit="full-width"
        onClick={onShowFilters}
        size="small"
      >
        Sort & filter
      </TextButton>
    </SearchContainer>
  </SectionContainer>)
}

export default CruiseSearchFiltersMobile
